import './WaiverAndReleaseFormScreen.scss';
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps} from "formik";
import React, {useCallback, useEffect, useRef, useState} from "react";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import _ from "lodash";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import * as Yup from "yup";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import CardComponent from "../../../shared/components/card/CardComponent";
import BackComponent from "../../../shared/components/back/BackComponent";
import {CommonService} from "../../../shared/services";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {Misc} from "../../../constants";
import {getSystemSettingDetails} from "../../../store/actions/user.action";
import {getClientBasicDetails} from "../../../store/actions/client.action";

interface WaiverAndReleaseFormScreenProps {

}

const WaiverAndReleaseOfLiabilityFormInitialValues: any = {
    client_name: "",
    date: new Date(),
    agreed_to_terms: false,
    agreed_to_waiver_expiry: false,
    agreed_to_renew_waiver: false,
    is_authorized_representative: false,
    representative_name: "",
    signature: ""
}
const WaiverAndReleaseOfLiabilityValidationSchema = Yup.object().shape({
    client_name: Yup.string().required('Client Name is required'),
    date: Yup.date().required('Date is required'),
    agreed_to_terms: Yup.boolean()
        .oneOf([true], 'You must agree to the terms'),
    agreed_to_waiver_expiry: Yup.boolean()
        .oneOf([true], 'You must agree to the waiver expiry'),
    agreed_to_renew_waiver: Yup.boolean()
        .oneOf([true], 'You must agree to the waiver renewal'),
    is_authorized_representative: Yup.boolean(),
    representative_name: Yup.string().when('is_authorized_representative', {
        is: true,
        then: Yup.string().required('Representative Name is required'),
        otherwise: Yup.string().notRequired(),
    }),
    signature: Yup.string().required('Signature is required'),
});


const WaiverAndReleaseFormScreen = (props: WaiverAndReleaseFormScreenProps) => {
    const {
        clientBasicDetails
    } = useSelector((state: IRootReducerState) => state.client);
    const dispatch = useDispatch();
    const [isWaiverFormSubmitting, setWaiverFormSubmitting] = useState(false);
    const [clientWaiverAndReleaseOfLiabilityFormInitialValues, setClientWaiverAndReleaseOfLiabilityFormInitialValues] = useState<any>(_.cloneDeep(WaiverAndReleaseOfLiabilityFormInitialValues));
    const formikRef = useRef<FormikProps<any>>(null);
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const {id} = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");


    useEffect(() => {
        if (clientBasicDetails) {
            setClientWaiverAndReleaseOfLiabilityFormInitialValues((prevValues: any) => ({
                ...prevValues,
                client_name: clientBasicDetails?.first_name + ' ' + clientBasicDetails?.last_name,
            }));
        }
    }, [clientBasicDetails]);

    const onSubmit = useCallback((values: any, {setErrors}: FormikHelpers<any>) => {
        let apiCall;
        setWaiverFormSubmitting(true);
        const payload = {...values};
        payload.date = CommonService.convertDateFormat(payload.date, "YYYY-MM-DD");
        if (mode==='renew' && id) {
            apiCall = CommonService._dashboard.WaiverAndReleaseForm(id, payload)
        } else {
            apiCall = CommonService._dashboard.AddWaiverAndReleaseForm(payload)
        }
        apiCall.then((response: IAPIResponseType<any>) => {
            CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
            setWaiverFormSubmitting(false);
            dispatch(getSystemSettingDetails());
            dispatch(getClientBasicDetails());
            navigate(CommonService._routeConfig.ClientResource());
        })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setWaiverFormSubmitting(false);
            });

    }, [navigate,mode, dispatch,id]);


    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);


    return (
        <div className="waiver-and-release-form-screen">
            <div className="list-screen__header">
                <div className="list-screen__header-title">
                    <BackComponent onClick={() => navigate(CommonService._routeConfig.ClientResource())}/>
                </div>
            </div>
            <CardComponent>
                <div className={'waiver-and-release-form-screen__header'}>Waiver and Release of Liability</div>
                <div className={'waiver-and-release-form-screen__description'}>
                    This <b>Agreement</b> acknowledges that athletic or physical therapy, fitness or performance
                    training,
                    wellness
                    care, and other related activities can involve physical, emotional, and psychological exertion,
                    strain,
                    and the potential risk of injury. By participating in any activities at Kinergy Sports Medicine and
                    Performance (hereafter referred to as “KINERGY”), you do so entirely at your own risk and agree to
                    assume all risks associated with your participation.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>1.Acknowledgement of Risk</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>You understand and acknowledge that
                    athletic or physical therapy and training activities, including but
                    not limited to, exercise routines, strength training, rehabilitation exercises, wellness training,
                    and
                    other activities offered at KINERGY, can be strenuous. These activities carry an inherent risk of
                    injury, illness, or even death, including the possibility of accidents or the sudden malfunctioning
                    of
                    equipment.<br/><br/>
                    You agree to voluntarily participate in these activities and assume all risks associated with such
                    activities, including but not limited to injury, accidents, or loss of personal property. You also
                    acknowledge that you are fully responsible for any personal belongings or property brought to
                    KINERGY.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>2.Medical Clearance</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    KINERGY strongly recommends that you obtain medical clearance from a licensed healthcare
                    professional
                    before using any exercise equipment or participating in any activities at KINERGY, particularly if
                    you
                    have any existing medical conditions, concerns, or a history of injury. KINERGY may, at its
                    discretion,
                    request medical clearance or advise against participation if deemed necessary for your safety.
                </div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>3.Personal Responsibility</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    You acknowledge that it is your responsibility to inform KINERGY's staff of any existing medical
                    conditions, injuries, limitations, or concerns that may affect your ability to participate in any
                    activities offered at KINERGY. You confirm that you are physically and mentally capable of
                    participating
                    in the activities you choose to engage in at KINERGY.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>4.Release of Liability</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    By signing this waiver, you, on behalf of yourself, your heirs, executors, personal representatives,
                    and
                    assigns, hereby release, waive, and discharge KINERGY, its owners, employees, agents, instructors,
                    and
                    affiliates from any and all liability for any injuries, damages, claims, or losses, including but
                    not
                    limited to personal injury, property damage, or loss, that may arise from your participation in any
                    activity at KINERGY, whether caused by negligence, breach of contract, or otherwise.<br/><br/>
                    This release applies to all claims or causes of action, including but not limited to negligence,
                    personal injury, or property damage, which may result from your use of the facility, its equipment,
                    or
                    participation in its programs.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>5.Indemnification</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    You agree to indemnify and hold harmless KINERGY, its owners, employees, instructors, agents, and
                    affiliates from any and all claims, demands, actions, liabilities or lawsuits arising out of your
                    participation in activities at KINERGY, including any claims made by third parties due to your
                    actions
                    or participation.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>6.Medical Emergency</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    In the event of a medical emergency, you authorize KINERGY's staff to perform or seek emergency
                    medical
                    treatment on your behalf, if necessary. You agree to bear all costs associated with such treatment
                    and
                    release KINERGY from any liability in this regard.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>7.Severability</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    If any provision of this Waiver and Release of Liability is found to be invalid or unenforceable by
                    a
                    court of competent jurisdiction, the remaining provisions shall remain in full force and effect, and
                    the
                    offending provisions or provisions severed from this Agreement.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>8.Duration and Renewal</div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    This Waiver and Release of Liability shall remain valid for one (1) year from the date of signing.
                    After
                    this period, you will be required to sign a new Waiver and Release of Liability form in order to
                    continue participation in activities at KINERGY.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>
                    9.Governing Law
                </div>
                <div className={'waiver-and-release-form-screen__pointers-text'}>
                    This Agreement will be governed by and construed in accordance with the laws of the state or
                    jurisdiction in which KINERGY operates. Any disputes arising from this Agreement shall be subject to
                    the
                    exclusive jurisdiction of the courts in that jurisdiction.
                </div>
                <div className={'waiver-and-release-form-screen__pointers'}>Acknowledgement and Agreement</div>
                <div className={'waiver-and-release-form-screen__pointers-text pdd-left-0'}>
                    By signing below, you acknowledge that you have read, fully understand, and voluntarily agree to the
                    terms and conditions of this Waiver and Release of Liability. You understand that this document is
                    legally binding, and you are waiving certain legal rights, including the right to sue. You further
                    acknowledge that this Agreement cannot be modified orally, and no oral representations have been
                    made to
                    alter its meaning.
                </div>

                <Formik
                    validationSchema={WaiverAndReleaseOfLiabilityValidationSchema}
                    initialValues={clientWaiverAndReleaseOfLiabilityFormInitialValues}
                    validateOnChange={false}
                    validateOnBlur={true}
                    enableReinitialize={true}
                    innerRef={formikRef}
                    validateOnMount={true}
                    onSubmit={onSubmit}
                >
                    {({values, validateForm, touched, errors, isValid, isSubmitting, setFieldValue}) => {
                        // eslint-disable-next-line react-hooks/rules-of-hooks
                        useEffect(() => {
                            validateForm();
                        }, [validateForm, values]);
                        return (
                            <Form className="t-form" noValidate={true}>
                                <div className="t-form-controls">
                                    <div className="ts-row pdd-top-20">
                                        <div className="ts-col-12">
                                            <Field name={'agreed_to_terms'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={"I acknowledge that I have read and agree to the statements as written above."}
                                                            formikField={field}
                                                            required={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'agreed_to_waiver_expiry'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={'I understand that this Waiver and Release of Liability form will remain valid for one (1) year from the date of signing.'}
                                                            formikField={field}
                                                            required={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'agreed_to_renew_waiver'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={'I also understand that I will need to sign a new Waiver and Release of Liability form upon the expiration of this form.'}
                                                            formikField={field}
                                                            required={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-12">
                                            <Field name={'is_authorized_representative'}
                                                   className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikCheckBoxComponent
                                                            label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                            formikField={field}
                                                            onChange={() => {
                                                                setFieldValue('representative_name', '');
                                                            }}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    <div className="ts-row">
                                        <div className="ts-col-md-12 ts-col-lg-6">
                                            <Field name={'client_name'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            id={"name_input"}
                                                            label={"Client's Name"}
                                                            placeholder={"Enter Client's Name"}
                                                            required={true}
                                                            disabled={true}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            titleCase={true}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="ts-col-md-12 ts-col-lg-6">
                                            <Field name={'date'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikDatePickerComponent
                                                            id={"dob_input"}
                                                            label={'Date'}
                                                            placeholder={'Enter Date'}
                                                            formikField={field}
                                                            fullWidth={true}
                                                            disabled={true}
                                                            readOnly={true}
                                                            // clearable={false}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                    </div>
                                    {
                                        values.is_authorized_representative && <div className="ts-row">
                                            <div className="ts-col-12">
                                                <Field name={'representative_name'} className="t-form-control">
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Authorized Representative Name'}
                                                                placeholder={'Enter Representative Name'}
                                                                required={true}
                                                                fullWidth={true}
                                                                formikField={field}
                                                                titleCase={true}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </div>
                                        </div>
                                    }
                                    {
                                        !values?.signature &&
                                        <div className="ts-row">
                                            <div className="ts-col-12">
                                                <FormControlLabelComponent
                                                    className={"font-weight-thin signature-label"}
                                                    label={"Signature of Client or Authorized Representative"}
                                                    required={true}/>
                                                <ButtonComponent
                                                    onClick={() => {
                                                        setIsSignaturePopupOpen(true);
                                                    }}
                                                    variant={'outlined'}
                                                    color={'accent'}
                                                    size={'medium'}
                                                >
                                                    Add Signature
                                                </ButtonComponent>
                                            </div>
                                            {
                                                (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                                <ErrorComponent
                                                    errorText={(_.get(errors, "signature"))}/>
                                            }
                                        </div>
                                    }
                                    {values?.signature &&
                                        <div className="ts-row">
                                            <div className={'ts-col-6 mrg-0'}>
                                                <ESignApprovalComponent isSigned={true}
                                                                        signature_url={values?.signature}
                                                />
                                                <div className={'signature-remove-button'}>
                                                    <LinkComponent onClick={() => {
                                                        setFieldValue('signature', "");
                                                    }
                                                    }>
                                                        Remove Signature
                                                    </LinkComponent>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    <div className="t-form-actions">
                                        <ButtonComponent
                                            id={"cancel_btn"}
                                            color={'accent'}
                                            className={'mrg-right-15'}
                                            variant={'outlined'}
                                            onClick={() => navigate(CommonService._routeConfig.ClientResource())}
                                        >
                                            Cancel
                                        </ButtonComponent>
                                        <ButtonComponent
                                            id={"submit_btn"}
                                            color={'accent'}
                                            type={"submit"}
                                            disabled={!isValid || isSubmitting}
                                            isLoading={isWaiverFormSubmitting}
                                        >
                                            Save
                                        </ButtonComponent>
                                    </div>
                                </div>
                            </Form>

                        )
                    }}
                </Formik>
            </CardComponent>
            <ModalComponent isOpen={isSignaturePopupOpen}
                            onClose={handleSignaturePopupClose}
                            size={"xl"}
            >
                <SignaturePopupComponent
                    onSign={(signImage: any) => {
                        if (formikRef?.current) {
                            formikRef.current.setFieldValue(
                                'signature',
                                signImage
                            );
                        }

                        handleSignaturePopupClose();
                    }}
                    handleSignaturePopupClose={handleSignaturePopupClose}
                />
            </ModalComponent>

        </div>
    )
}

export default WaiverAndReleaseFormScreen
